"use client";

// src/checkbox.tsx
import * as React2 from "react";
import {
  Checkbox as CheckboxBase
} from "@stepstone-genesis/reakit-library";
import { FlexItem } from "@stepstone-genesis/components-flex";
import {
  Base as Base2,
  systemForwardRef,
  useStyleConfig
} from "@stepstone-genesis/system";
import { FormLabel } from "@stepstone-genesis/components-form-label";
import { FormHelpText } from "@stepstone-genesis/components-form-help-text";
import { CheckIcon, MinusIcon } from "@stepstone-genesis/icons";
import { useUID as useUID2 } from "@stepstone-genesis/uid-library";
import {
  FormFieldControl,
  useFormStateHandler,
  useFormStateHandlerContext as useFormStateHandlerContext2
} from "@stepstone-genesis/components-form-state-handler";

// src/styles.ts
import { srOnlyStyles } from "@stepstone-genesis/components-sronly";
var root = ({ theme }) => {
  var _a, _b, _c, _d;
  return {
    appearance: "none",
    size: "100%",
    backgroundColor: "transparent",
    position: "relative",
    zIndex: "zIndex10",
    borderRadius: "radiusM",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "colorBorderDefault",
    // outlineColor: 'transparent',
    // outlineWidth: '2px',
    // outlineStyle: 'solid',
    // boxShadow: `0 0 0 ${theme?.space?.spaceXs} transparent`,
    transition: "border-color ease 200ms",
    cursor: "pointer",
    "& ~[data-checked]": { display: "none" },
    "& ~[data-mixed]": { display: "none" },
    _notChecked: {
      backgroundColor: "colorWhite"
    },
    _checked: {
      color: "colorWhite",
      borderColor: "colorActionableDefault",
      backgroundColor: "transparent",
      "~ [data-checked]": {
        display: "block",
        backgroundColor: "colorActionableDefault"
      },
      _mixed: {
        "~ [data-checked]": { display: "none" }
      },
      _invalid: {
        borderColor: "colorStatusNegativeDefault",
        "& ~[data-checked]": {
          backgroundColor: "colorStatusNegativeDefault"
        },
        "& ~[data-mixed]": {
          backgroundColor: "colorStatusNegativeDefault"
        }
      }
    },
    _focusVisible: {
      outline: "outlineDefault",
      outlineOffset: "2px",
      boxShadow: "boxShadowFocusWhite"
    },
    _invalid: {
      borderColor: "colorStatusNegativeDefault"
    },
    _active: {
      outlineColor: "transparent",
      boxShadow: `0 0 0 ${(_a = theme == null ? void 0 : theme.space) == null ? void 0 : _a.spaceXs} ${(_b = theme == null ? void 0 : theme.colors) == null ? void 0 : _b.colorStatusInfoLight}`
    },
    _hover: {
      boxShadow: `0 0 0 ${(_c = theme == null ? void 0 : theme.space) == null ? void 0 : _c.spaceXs} ${(_d = theme == null ? void 0 : theme.colors) == null ? void 0 : _d.colorOffWhite}`,
      outlineColor: "transparent",
      _invalid: {
        borderColor: "colorStatusNegativeDefault"
      }
    },
    _mixed: {
      borderColor: "colorActionableDefault",
      backgroundColor: "transparent",
      "~ [data-mixed]": {
        display: "block",
        backgroundColor: "colorActionableDefault"
      },
      _invalid: {
        borderColor: "colorStatusNegativeDefault",
        "& ~[data-checked]": { backgroundColor: "colorStatusNegativeDefault" },
        "& ~[data-mixed]": { backgroundColor: "colorStatusNegativeDefault" }
      }
    },
    _disabled: {
      cursor: "not-allowed",
      borderColor: "colorBorderDefault",
      boxShadow: "boxShadowNone",
      _notChecked: {
        backgroundColor: "colorOffWhite",
        _after: {
          size: "auto"
        }
      },
      _checked: {
        backgroundColor: "transparent",
        "& ~[data-checked]": {
          backgroundColor: "colorGreyLight"
        },
        borderColor: "colorGreyLight",
        _after: {
          size: "size10"
        }
      },
      _mixed: {
        "& ~[data-mixed]": {
          backgroundColor: "colorGreyLight"
        }
      }
    }
  };
};
var checkboxContainerStyles = ({
  props: { isHidden }
}) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  borderRadius: "radiusM",
  transition: "background-color ease 200ms",
  cursor: "pointer",
  ...isHidden ? srOnlyStyles.root : {},
  _disabled: {
    cursor: "not-allowed"
  }
});
var iconStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "radiusM",
  size: "100%",
  svg: {
    color: "colorWhite",
    boxSizing: "content-box"
  }
};
var sizes = ({ theme }) => {
  var _a;
  return {
    medium: {
      size: "size30",
      svg: {
        size: "iconSizeL"
      }
    },
    large: {
      size: "size30",
      marginTop: "spaceNone",
      svg: {
        size: "iconSizeM",
        padding: `calc(${(_a = theme == null ? void 0 : theme.space) == null ? void 0 : _a.spaceXs} / 2)`
      }
    }
  };
};
var variants = ({
  theme
}) => {
  var _a;
  return {
    inverted: {
      borderColor: "colorWhite",
      _checked: {
        borderColor: "colorActionableLightDefault",
        "~ [data-checked]": {
          backgroundColor: "colorActionableLightDefault",
          svg: {
            color: "colorActionableDefault"
          }
        }
      },
      _mixed: {
        borderColor: "colorActionableLightDefault",
        "~ [data-mixed]": {
          backgroundColor: "colorActionableLightDefault",
          svg: {
            color: "colorActionableDefault"
          }
        }
      },
      _hover: {
        // TODO: We don't have opacity token yet, so hardcoded the boxShadow temporarily.
        boxShadow: `0 0 0 ${(_a = theme.space) == null ? void 0 : _a.spaceXs} rgba(255, 255, 255, 0.3)`
      },
      _disabled: {
        _checked: {
          "~ [data-checked]": {
            svg: {
              color: "colorWhite"
            }
          }
        },
        _mixed: {
          "~ [data-mixed]": {
            svg: {
              color: "colorWhite"
            }
          }
        }
      },
      _invalid: {
        _checked: {
          "~ [data-checked]": {
            svg: {
              color: "colorWhite"
            }
          }
        },
        _mixed: {
          "~ [data-mixed]": {
            svg: {
              color: "colorWhite"
            }
          }
        }
      }
    }
  };
};
var styles = {
  checkboxContainerStyles,
  iconStyles,
  root,
  sizes,
  variants
};

// src/context.tsx
import * as React from "react";
import { FormControlGroup } from "@stepstone-genesis/components-form-control-group";
import { useFormStateHandlerContext } from "@stepstone-genesis/components-form-state-handler";
import { useUID } from "@stepstone-genesis/uid-library";
import { Base } from "@stepstone-genesis/system";
import {
  useCheckboxState as useCheckboxBaseState
} from "@stepstone-genesis/reakit-library";
var CheckboxGroupAttributesContext = React.createContext({});
var useCheckboxGroupAttributesContext = () => {
  const context = React.useContext(CheckboxGroupAttributesContext);
  if (!context) {
    throw new Error(
      "useCheckboxGroupAttributesContext must be used within CheckboxGroupAttributesContext.Provider"
    );
  }
  return context;
};
var CheckboxGroupContext = React.createContext(
  null
);
var useCheckboxGroupContext = () => {
  const context = React.useContext(CheckboxGroupContext);
  return context;
};
var CheckboxGroup = ({
  id: identifier,
  children,
  value,
  defaultValue,
  size,
  variant,
  onChange,
  isDisabled = false,
  isInvalid = false,
  isRequired = false,
  hideLabel = false,
  direction = "vertical",
  name,
  label,
  helpText = "",
  errorText = "",
  isWithinFormScope,
  ...initialState
}) => {
  const uuid = useUID();
  const id = identifier != null ? identifier : `stepstone-checkbox-${uuid}`;
  const groupFeedbackId = isInvalid ? `stepstone-checkbox-${uuid}-feedback` : void 0;
  const baseState = useCheckboxBaseState({
    state: defaultValue ? defaultValue : true,
    ...initialState
  });
  const originalState = React.useMemo(
    () => ({
      ...baseState,
      baseId: id != null ? id : baseState.baseId,
      size,
      variant,
      state: value != null ? value : baseState.state,
      setState: onChange != null ? onChange : baseState.setState
    }),
    [baseState, id, size, variant, value, onChange]
  );
  const attributes = React.useMemo(
    () => ({
      isDisabled,
      isInvalid,
      isRequired,
      groupFeedbackId,
      name
    }),
    [isDisabled, isInvalid, isRequired, groupFeedbackId, name]
  );
  return /* @__PURE__ */ React.createElement(CheckboxGroupContext.Provider, { value: originalState }, /* @__PURE__ */ React.createElement(CheckboxGroupAttributesContext.Provider, { value: attributes }, /* @__PURE__ */ React.createElement(
    FormControlGroup,
    {
      hideLabel,
      label,
      direction,
      helpText,
      errorText,
      isRequired,
      isDisabled,
      isInvalid,
      groupFeedbackId,
      isWithinFormScope,
      size,
      wrapChildren: (wrappedChildren) => /* @__PURE__ */ React.createElement(Base, { role: "group", "aria-label": label, ...originalState }, wrappedChildren)
    },
    children
  )));
};
var useCheckboxSlot = () => {
  const checkboxContext = useCheckboxGroupContext();
  const attributes = useCheckboxGroupAttributesContext();
  const context = useFormStateHandlerContext();
  const state = React.useMemo(
    () => ({
      checkboxContext,
      attributes,
      context
    }),
    [checkboxContext, attributes, context]
  );
  return state;
};

// src/checkbox.tsx
var Checkbox = systemForwardRef(
  function Checkbox2(props, ref) {
    const {
      "aria-hidden": ariaHidden,
      "aria-disabled": ariaDisabled,
      size = "medium",
      variant = "default"
    } = props;
    const { FORM_CHECKBOX_CONTAINER } = useStyleConfig(
      "FORM_CHECKBOX_CONTAINER",
      {
        styles: {
          base: styles.checkboxContainerStyles,
          responsiveVariants: [
            {
              id: "form-checkbox-container-size",
              prop: size,
              variants: styles.sizes
            }
          ]
        },
        props: {
          isHidden: Boolean(ariaHidden)
        }
      }
    );
    const { FORM_CHECKBOX } = useStyleConfig("FORM_CHECKBOX", {
      styles: {
        base: styles.root,
        responsiveVariants: [
          {
            id: "form-checkbox-container-size",
            prop: size,
            variants: styles.sizes
          },
          {
            id: "form-checkbox-container-variant",
            prop: variant,
            variants: styles.variants
          }
        ]
      }
    });
    const { FORM_CHECKBOX_ICON } = useStyleConfig("FORM_CHECKBOX_ICON", {
      styles: {
        base: styles.iconStyles,
        responsiveVariants: [
          {
            id: "form-checkbox-container-size",
            prop: size,
            variants: styles.sizes
          }
        ]
      }
    });
    return /* @__PURE__ */ React2.createElement(
      Base2,
      {
        "aria-disabled": ariaDisabled,
        as: "span",
        element: "FORM_CHECKBOX_CONTAINER",
        __root: FORM_CHECKBOX_CONTAINER.styles,
        sx: props.sx
      },
      /* @__PURE__ */ React2.createElement(
        Base2,
        {
          as: "input",
          ref,
          type: "checkbox",
          element: "FORM_CHECKBOX",
          __root: FORM_CHECKBOX.styles,
          ...props
        }
      ),
      /* @__PURE__ */ React2.createElement(
        CheckIcon,
        {
          element: "FORM_CHECKBOX_ICON",
          sx: FORM_CHECKBOX_ICON.styles,
          "data-checked": "",
          "aria-hidden": "true"
        }
      ),
      /* @__PURE__ */ React2.createElement(
        MinusIcon,
        {
          element: "FORM_CHECKBOX_ICON",
          sx: FORM_CHECKBOX_ICON.styles,
          "data-mixed": "",
          "aria-hidden": "true"
        }
      )
    );
  }
);
var FormCheckboxElement = systemForwardRef(function FormCheckboxElement2({ name, size, ...props }, ref) {
  var _a;
  const { context, groupFeedbackId, isIndeterminate, ...rest } = props;
  const ctx = context == null ? void 0 : context.formElementProps(props, ref);
  const describeGroupError = groupFeedbackId ? ctx && ctx["aria-describedby"] ? `${groupFeedbackId} ${ctx["aria-describedby"]}` : groupFeedbackId : (_a = ctx && ctx["aria-describedby"]) != null ? _a : void 0;
  return /* @__PURE__ */ React2.createElement(
    CheckboxBase,
    {
      ...rest,
      ...ctx,
      as: "input",
      size: void 0
    },
    (baseCheckboxProps) => {
      return /* @__PURE__ */ React2.createElement(
        Checkbox,
        {
          ...baseCheckboxProps,
          size,
          name: name != null ? name : baseCheckboxProps.name,
          "aria-describedby": describeGroupError,
          "aria-checked": isIndeterminate ? "mixed" : baseCheckboxProps.checked
        }
      );
    }
  );
});
var FormElementNonContext = systemForwardRef(
  function FormElementNonContext2(props, ref) {
    const nonContextFormStateHandler = useFormStateHandler(props);
    return /* @__PURE__ */ React2.createElement(
      FormCheckboxElement,
      {
        context: nonContextFormStateHandler,
        ref,
        ...props
      }
    );
  }
);
var FormCheckboxPrimitive = systemForwardRef(
  function FormCheckbox(props, ref) {
    const withinContextFormStateHandler = useFormStateHandlerContext2();
    return withinContextFormStateHandler ? /* @__PURE__ */ React2.createElement(
      FormCheckboxElement,
      {
        context: withinContextFormStateHandler,
        ref,
        size: withinContextFormStateHandler.size,
        ...props
      }
    ) : /* @__PURE__ */ React2.createElement(FormElementNonContext, { ref, ...props });
  }
);
var FormCheckbox2 = systemForwardRef(function FormCheckbox3(props, ref) {
  var _a;
  const {
    helpText,
    children,
    isDisabled,
    isInvalid,
    id,
    name,
    size,
    variant,
    ...rest
  } = props;
  const uniqueId = useUID2();
  const checkboxContext = useCheckboxGroupContext();
  const attributes = useCheckboxGroupAttributesContext();
  const identifier = (_a = id != null ? id : checkboxContext && `${checkboxContext == null ? void 0 : checkboxContext.baseId}-${uniqueId}`) != null ? _a : `stepstone-checkbox-${uniqueId}`;
  const sizeVariant = size != null ? size : checkboxContext == null ? void 0 : checkboxContext.size;
  const checkboxVariant = variant != null ? variant : checkboxContext == null ? void 0 : checkboxContext.variant;
  return /* @__PURE__ */ React2.createElement(React2.Fragment, null, /* @__PURE__ */ React2.createElement(
    FormFieldControl,
    {
      id: identifier,
      isDisabled: isDisabled != null ? isDisabled : attributes.isDisabled,
      isInvalid: isInvalid != null ? isInvalid : attributes.isInvalid,
      role: void 0,
      sx: { display: "flex", flexWrap: "nowrap" }
    },
    /* @__PURE__ */ React2.createElement(FlexItem, { flxShrink: 0, flxSelf: "flex-start" }, /* @__PURE__ */ React2.createElement(
      FormCheckboxPrimitive,
      {
        ...rest,
        ...checkboxContext,
        name: name != null ? name : attributes.name,
        groupFeedbackId: attributes.groupFeedbackId,
        ref,
        size: sizeVariant,
        variant: checkboxVariant
      }
    )),
    /* @__PURE__ */ React2.createElement(FlexItem, { flxShrink: 1, flxGrow: 1, flxBasis: "0%" }, /* @__PURE__ */ React2.createElement(
      FormLabel,
      {
        pl: "spaceS",
        size: sizeVariant,
        fontWeight: "fontWeightNormal"
      },
      children
    ), helpText && /* @__PURE__ */ React2.createElement(FormHelpText, { pl: "spaceS", size: sizeVariant }, helpText))
  ));
});
export {
  CheckboxGroup,
  FormCheckbox2 as FormCheckbox,
  FormCheckboxPrimitive,
  useCheckboxGroupAttributesContext,
  useCheckboxGroupContext,
  useCheckboxSlot
};
